import {
  HUAFENDA84_LAYOUT_MATRIX,
  HUAFENDA87_LAYOUT_MATRIX,
  HUAFENDA_M98_LAYOUT_MATRIX,
  HUAFENDA_DK108_LAYOUT_MATRIX,
  HUAFENDA_M98TFT_LAYOUT_MATRIX,
  LAYOUT_CODE_TO_WEB_KEY,
  resolveHuafendaKeyIndex,
} from "./huafendaKeyIndexMap";
import { shouldUseHfdRhythmLayoutOrder } from "./hfdSdkRuntime";

export interface RhythmLayoutKey {
  code: string;
  row: number;
  col: number;
  keyCode: number;
  ledIndex: number;
  label: string;
}

type LayoutCellLike = {
  code?: string;
  label?: string;
  keyCode?: number;
  originalRow?: number;
  originalCol?: number;
};

function isGapCell(cell: LayoutCellLike): boolean {
  const code = cell.code ?? "";
  return (
    !code ||
    code === "__gap__" ||
    code === "__NOT_EXIST_" ||
    code === "KEY_0" ||
    (cell.keyCode ?? 0) <= 0 ||
    (cell.originalCol ?? -1) < 0
  );
}

function buildRhythmLayoutKeysFromKeycodeMatrix(
  matrix: Readonly<number[][]>
): RhythmLayoutKey[] {
  const keys: RhythmLayoutKey[] = [];
  matrix.forEach((row, rowIndex) => {
    row.forEach((layoutCode, colIndex) => {
      if (layoutCode <= 0) return;
      const code = LAYOUT_CODE_TO_WEB_KEY[layoutCode];
      const ledIndex = resolveHuafendaKeyIndex(layoutCode);
      if (!code || ledIndex < 0) return;
      keys.push({
        code,
        row: rowIndex,
        col: colIndex,
        keyCode: layoutCode,
        ledIndex,
        label: code,
      });
    });
  });
  return keys;
}

function resolveHuafendaRhythmLayoutFallback(
  keyboardType?: string
): RhythmLayoutKey[] {
  const kt = String(keyboardType ?? "");
  if (kt === "84" || kt === "deep80max") {
    return buildHuafenda84RhythmLayoutKeys();
  }
  if (kt === "M98") {
    return buildHuafendaM98RhythmLayoutKeys();
  }
  if (kt === "M98TFT" || kt === "M98 8K") {
    return buildHuafendaM98TftRhythmLayoutKeys();
  }
  if (kt === "DK108") {
    return buildHuafendaDk108RhythmLayoutKeys();
  }
  return buildHuafenda87RhythmLayoutKeys();
}

/** 从预览/设备 KeyConfig[][] 构建律动键位（与 ai-hub CanonicalKey row/col 对齐） */
export function buildRhythmLayoutKeysFromMatrix(
  layout: unknown[] | null | undefined,
  keyboardType?: string
): RhythmLayoutKey[] {
  if (!layout || !Array.isArray(layout) || layout.length === 0) {
    return resolveHuafendaRhythmLayoutFallback(keyboardType);
  }

  const keys: RhythmLayoutKey[] = [];
  for (const row of layout) {
    if (!Array.isArray(row)) continue;
    for (const raw of row) {
      if (!raw || typeof raw !== "object") continue;
      const cell = raw as LayoutCellLike;
      if (isGapCell(cell)) continue;
      const keyCode = cell.keyCode ?? 0;
      const ledIndex = resolveHuafendaKeyIndex(keyCode);
      keys.push({
        code: cell.code!,
        row: cell.originalRow ?? 0,
        col: cell.originalCol ?? 0,
        keyCode,
        ledIndex,
        label: cell.label ?? cell.code ?? "",
      });
    }
  }

  return keys.length > 0
    ? keys
    : resolveHuafendaRhythmLayoutFallback(keyboardType);
}

/** 华奋达 X87 / X87 V2 出厂骨架（无 SDK 布局时的回退） */
export function buildHuafenda87RhythmLayoutKeys(): RhythmLayoutKey[] {
  return buildRhythmLayoutKeysFromKeycodeMatrix(HUAFENDA87_LAYOUT_MATRIX);
}

/** SMART875 / 84% TKL 出厂骨架 */
export function buildHuafenda84RhythmLayoutKeys(): RhythmLayoutKey[] {
  return buildRhythmLayoutKeysFromKeycodeMatrix(HUAFENDA84_LAYOUT_MATRIX);
}

/** M98 出厂骨架（含小键盘区） */
export function buildHuafendaM98RhythmLayoutKeys(): RhythmLayoutKey[] {
  return buildRhythmLayoutKeysFromKeycodeMatrix(HUAFENDA_M98_LAYOUT_MATRIX);
}

/** M98 Screen 出厂骨架 */
export function buildHuafendaM98TftRhythmLayoutKeys(): RhythmLayoutKey[] {
  return buildRhythmLayoutKeysFromKeycodeMatrix(HUAFENDA_M98TFT_LAYOUT_MATRIX);
}

/** DK108 出厂骨架（108 键全尺寸） */
export function buildHuafendaDk108RhythmLayoutKeys(): RhythmLayoutKey[] {
  return buildRhythmLayoutKeysFromKeycodeMatrix(HUAFENDA_DK108_LAYOUT_MATRIX);
}

export function rhythmLayoutKeysToLedIndexMap(
  keys: RhythmLayoutKey[]
): Record<string, number> {
  const map: Record<string, number> = {};
  for (const key of keys) {
    if (key.ledIndex >= 0) map[key.code] = key.ledIndex;
  }
  return map;
}

/**
 * 华奋达律动下发键序：
 * - 旧 2.4G：须与 KEYCODE_MAP_HFD_LAYOUT 出厂矩阵遍历序一致（勿用 SDK ledBeads / KeyConfig 遍历序）。
 * - 有线 / 新 2.4G：按 keyIndex 组包，键序不影响实体；仍用出厂矩阵保证 ledIndex 完整。
 */
export function resolveHfdRhythmKeysForMusicSend(
  equipment: Record<string, unknown> | null | undefined,
  layout: unknown[] | null | undefined,
  keyboardType?: string
): RhythmLayoutKey[] {
  const kt = String(keyboardType ?? "").trim();
  if (shouldUseHfdRhythmLayoutOrder(equipment)) {
    return resolveHuafendaRhythmLayoutFallback(kt);
  }
  const fromSkeleton = resolveHuafendaRhythmLayoutFallback(kt);
  if (fromSkeleton.length > 0) {
    return fromSkeleton;
  }
  return buildRhythmLayoutKeysFromMatrix(layout, kt);
}
